import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api.js';
import { PageLayout } from '@/components/layout/PageLayout.js';
import { NAV_LABELS } from '@/lib/nav-labels.js';

type Ferramenta = {
  id: string;
  slug: string;
  tipo: string;
  nome: string;
  descricao: string | null;
  perguntas: number;
  tempo_min: number;
  acesso: boolean;
};

const TIPO_LABEL: Record<string, string> = {
  DIAGNOSTICO: 'Diagnóstico',
  QUIZ: 'Quiz',
  PESQUISA: 'Pesquisa',
};

export function BibliotecaPage() {
  const [tipoFilter, setTipoFilter] = useState<string>('');
  const upgradeUrl =
    import.meta.env.VITE_COMMERCIAL_OS_MARKETING_URL ?? 'https://os.hack.net.br/catalogo-saas';

  const { data, isLoading, error } = useQuery({
    queryKey: ['ferramentas', tipoFilter],
    queryFn: async () => {
      const qs = tipoFilter ? `?tipo=${tipoFilter}` : '';
      return (await api.get<Ferramenta[]>(`/ferramentas${qs}`)).data;
    },
  });

  if (isLoading) {
    return <PageLayout title={NAV_LABELS.biblioteca5Forcas}>Carregando…</PageLayout>;
  }
  if (error) {
    return (
      <PageLayout title={NAV_LABELS.biblioteca5Forcas}>
        <p className="text-feedback">Não foi possível carregar a biblioteca.</p>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      title={NAV_LABELS.biblioteca5Forcas}
      description="Ferramentas do método As 5 Forças disponíveis no seu plano."
    >
      <div className="mb-6 flex flex-wrap gap-2">
        {['', 'DIAGNOSTICO', 'QUIZ', 'PESQUISA'].map((t) => (
          <button
            key={t || 'all'}
            type="button"
            className={`rounded-element border px-3 py-1 text-xs font-bold uppercase ${
              tipoFilter === t ? 'border-accent text-accent' : 'border-ha-border2 text-ha-muted'
            }`}
            onClick={() => setTipoFilter(t)}
          >
            {t ? TIPO_LABEL[t] : 'Todas'}
          </button>
        ))}
      </div>

      <ul className="grid gap-4 md:grid-cols-2">
        {data?.map((f) => (
          <li key={f.id} className="rounded-card border border-ha-border2 bg-ha-card p-5">
            <span className="font-mono text-[10px] uppercase text-ha-muted">{TIPO_LABEL[f.tipo] ?? f.tipo}</span>
            <h3 className="mt-1 font-semibold">{f.nome}</h3>
            {f.descricao && <p className="mt-2 text-sm text-ha-subtle">{f.descricao}</p>}
            <p className="mt-2 text-xs text-ha-muted">
              {f.perguntas} perguntas · ~{f.tempo_min} min
            </p>
            {f.acesso ? (
              <Link
                to={f.slug === '5forcas' ? '/metodo/diagnostico' : `/ferramenta?slug=${f.slug}`}
                className="mt-4 inline-block text-xs font-bold uppercase text-accent underline"
              >
                Iniciar
              </Link>
            ) : (
              <div className="mt-4">
                <span className="rounded bg-ha-card2 px-2 py-1 text-[10px] font-bold uppercase text-ha-muted">
                  Não incluído no seu plano
                </span>
                <a
                  href={upgradeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="ml-2 text-xs font-bold uppercase text-accent underline"
                >
                  Saiba mais
                </a>
              </div>
            )}
          </li>
        ))}
      </ul>
    </PageLayout>
  );
}
